import { FomanticShim, Nullable, PartialNullable, RadPromiseLike } from "../../Extensions";
import { ArrayHelper } from "../../Helpers/ArrayHelper";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { PromiseHelper } from "../../Helpers/PromiseHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { WindowShim } from "../../RadWindow";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { DialogResultEnum } from "../Dialogs/DialogResultEnum";
import { PageManager } from "../PageManager";
import { IPage } from "../Pages/Page";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "./ContainerControl";
import { FOCUSABLE_ELEMENTS_SELECTOR, IControl } from "./Control";
import { IDialog } from "./Dialog";
import { IDialog2 } from "./Dialog2";
import { NAMESPACE } from "./_Namespace";

import "./PageDimmer.scss";

const
    radWindow = WindowShim();

export type PageDimmerPropertyValues =
    ContainerControlPropertyValues
    & {
        animationIn: string;
        animationOut: string;
        repositionChildren: boolean;
    };

export type PageDimmerProperties = {
    [name in keyof PageDimmerPropertyValues]: Property<PageDimmerPropertyValues[name]>
};

export interface IPageDimmer extends IContainerControl, PageDimmerProperties
{
    hideAllAsync(): void;
    hideElementOrControlAsync($$elementOrControl: JNative<HTMLElement> | IControl): Promise<void>;
    showElementOrControlAsync($$elementOrControl: JNative<HTMLElement> | IControl): Promise<void>;
    shown(): boolean;
}

export class PageDimmer extends ContainerControl implements IPageDimmer
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PageDimmer";
    public static override className = "PageDimmer";

    // #region Global Variables

    private static dimmersActive: number = 0;
    private static dimmerWrapperElement: HTMLElement;

    // #endregion

    // #region Constructor

    static {
        $$(document).on("seriouslyrad.ui.Application:Unauthenticated", () =>
        {
            this.dimmersActive = 0;

            $$(this.dimmerWrapperElement)
                .children()
                .forEach(item => $$(item).detach());
        });
    }

    // #endregion

    // #region Public Methods

    public static async closeAllDialogsAsync()
    {
        if (!PageDimmer.dimmerWrapperElement)
            return;

        const
            dialogClosePromises: RadPromiseLike<any>[] = [];

        $$(PageDimmer.dimmerWrapperElement)
            .children()
            .forEach(item =>
            {
                const
                    pageDimmer = $$(item).radControl();

                if (!(pageDimmer instanceof PageDimmer))
                    return;

                pageDimmer.controlsGet()
                    .forEach(itemInner =>
                    {
                        if (ObjectHelper.classInheritsClassName<IDialog>(itemInner, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Dialog.Dialog"))
                        {
                            itemInner.dialogResult = DialogResultEnum.cancel;
                            itemInner.promiseData = null;
                            dialogClosePromises.push(itemInner.closeAsync());
                        }

                        if (ObjectHelper.classInheritsClassName<IDialog2>(itemInner, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Dialog2.Dialog2"))
                            dialogClosePromises.push(itemInner.closeAsync());
                    });
            });

        await Promise.all(dialogClosePromises);
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PageDimmerPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                animationIn: "fade in",
                animationOut: "fade out",
                repositionChildren: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$currentElement: Nullable<JNative<HTMLElement>>;
    protected currentControl: Nullable<IControl>;

    // #endregion

    // #region Properties

    public animationIn!: PropertyString;
    public animationOut!: PropertyString;
    public repositionChildren!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PageDimmerPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-page-dimmer ui page dimmer");
        this.$$currentElement = null;
        this.currentControl = null;
    }

    // #endregion

    // #region Public Methods

    public hideAllAsync()
    {
        if (PageDimmer.dimmersActive === 0)
            return Promise.resolve();

        return (
            this._currentElementHideAsync()
                .then(() =>
                {
                    const
                        $$element = this.$$element.get();

                    if ($$element)
                    {
                        for (let child of $$element.children())
                        {
                            const
                                $$child = $$(child),
                                control = $$child.radControl();

                            if (control)
                                control.detach();
                            else
                                $$child.detach();
                        }
                    }

                    this.$$currentElement = null;
                    this._hide();
                })
        );
    }
    public hideElementOrControlAsync($$elementOrControl: JNative<HTMLElement> | IControl): Promise<void>
    {
        if (PageDimmer.dimmersActive === 0)
            return Promise.resolve();

        const
            $$elementToHide = this._getElementFromElementOrControl($$elementOrControl),
            controlToHide = $$elementToHide?.radControl();

        if (!$$elementToHide || !$$elementToHide.length)
            return Promise.reject("Unable to determine $$elementOrControl.");

        if (!$$elementToHide.parent().is(this.$$element.get()))
            return Promise.reject("$$elementOrControl is not a direct child of the dimmer.");

        if (!$$elementToHide.is(this.$$currentElement))
        {
            const
                control = $$elementToHide.radControl();

            if (control)
                control.detach();
            else
                $$elementToHide.detach();

            return Promise.resolve();
        }

        return new Promise((resolve, reject) =>
        {
            this._currentElementHideAsync()
                .then(() =>
                {
                    if (controlToHide)
                        controlToHide.detach();
                    else
                        $$elementToHide?.detach();

                    const
                        $$lastElement = this.$$element.get()?.children().last();

                    if ($$lastElement && $$lastElement.length)
                    {
                        if ($$lastElement.is(this.$$currentElement))
                        {
                            resolve();
                            return;
                        }

                        this.$$currentElement = this.$$element.get()?.children().last() ?? null;
                        this.currentControl = this.$$currentElement?.radControl() ?? null;

                        this._currentElementShowAsync()
                            .then(() =>
                            {
                                resolve();
                            })
                            .catch(error =>
                            {
                                reject(error);
                            });
                    }
                    else
                    {
                        this.$$currentElement = null;
                        this.currentControl = null;
                        this._hide();
                        resolve();
                    }
                });
        });
    }
    public showElementOrControlAsync($$elementOrControl: JNative<HTMLElement> | IControl): Promise<void>
    {
        const
            $$elementToShow = this._getElementFromElementOrControl($$elementOrControl);

        if (!$$elementToShow || !$$elementToShow.length)
            return Promise.reject("Unable to determine $$elementOrControl.");

        if ($$elementToShow.parent().is(this.$$element.get()))
            return Promise.reject("$$elementOrControl is already a direct child of the dimmer.");

        if ($$elementToShow.is(this.$$currentElement))
            return Promise.resolve();

        $$(document).trigger("seriouslyrad.ui.web.PageDimmer:showingElementOrControl");

        return (
            this._currentElementHideAsync()
                .then<void>(() =>
                {
                    this._show();

                    $$elementToShow?.appendTo(this.$$element.get());
                    this.$$currentElement = $$elementToShow;
                    this.currentControl = this.$$currentElement?.radControl() ?? null;

                    if (ObjectHelper.isAssignableTo<IPage>(this.currentControl, "@seriously-rad/seriouslyrad-framework-core/Ui/Pages/Page.Page"))
                        this.$$element.get()?.css({ "overflow-y": this.currentControl.scrollable.get() ? "hidden" : "scroll" });
                    else
                        this.$$element.get()?.css({ "overflow-y": StringHelper.empty });

                    return this._currentElementShowAsync();
                })
        );
    }
    public shown()
    {
        return this.hasCssClass("visible active");
    }

    // #endregion

    // #region Protected Methods

    protected _animationInChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _animationOutChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _currentElementHideAsync(): Promise<void>
    {
        return new Promise(resolve =>
        {
            const
                $$currentElement = this.$$currentElement,
                currentControl = this.currentControl;

            if ($$currentElement)
            {
                let
                    animationOut = this.animationOut.get();

                const
                    currentControlAsAny = this.currentControl as any;

                if (currentControlAsAny?.dialogAnimationOut?.get !== void 0)
                    animationOut = currentControlAsAny.dialogAnimationOut.get();
                else if (currentControlAsAny?.animationOut?.get !== void 0)
                    animationOut = currentControlAsAny.animationOut.get();

                if (animationOut && $$currentElement.css("display") !== "none")
                {
                    FomanticShim($$currentElement)
                        .transition("stop all")
                        .transition({
                            animation: animationOut + " out",
                            onComplete()
                            {
                                if (currentControl)
                                    currentControl.hide();
                                else if ($$currentElement)
                                    $$currentElement.hide();

                                resolve();
                            }
                        });
                }
                else
                {
                    setTimeout(() =>
                    {
                        if (currentControl)
                            currentControl.hide();
                        else if ($$currentElement)
                            $$currentElement.hide();

                        resolve();
                    });
                }
            }
            else
                resolve();
        });
    }
    protected _currentElementReposition(animate: boolean)
    {
        if (!this.repositionChildren.get())
            return;

        if (!this.$$currentElement || !this.$$currentElement.length)
            return;

        const
            $$element = this.$$element.get(),
            elementWidthWithoutScrollbars = $$element?.prop("clientWidth") as number | undefined;

        if (!elementWidthWithoutScrollbars)
            return;

        if (animate)
        {
            this.$$currentElement
                .stop()
                .animate({
                    top: (this.$$currentElement.outerHeight(true) ?? 0) < ($$element?.height() ?? 0) * 0.7 ? ($$element?.height() ?? 0) * 0.3 : 0,
                    left: (elementWidthWithoutScrollbars - (this.$$currentElement.outerWidth() ?? 0)) / 2
                });
        }
        else
        {
            this.$$currentElement
                .stop()
                .css({
                    top: (this.$$currentElement.outerHeight(true) ?? 0) < ($$element?.height() ?? 0) * 0.7 ? ($$element?.height() ?? 0) * 0.3 : 0,
                    left: (elementWidthWithoutScrollbars - (this.$$currentElement.outerWidth() ?? 0)) / 2
                });
        }
    }
    protected _currentElementShowAsync(): Promise<void>
    {
        const
            $$currentElement = this.$$currentElement,
            currentControl = this.currentControl;

        return new Promise((resolve, reject) =>
        {
            if ($$currentElement)
            {
                if (currentControl)
                    currentControl.show();
                else
                    $$currentElement.show();

                this._currentElementReposition(false);

                $$currentElement.css("visibility", "hidden");
                $$currentElement.removeClass("hidden");

                let
                    initialPromise = Promise.resolve();

                if (currentControl)
                {
                    if ((currentControl as any).loadAsync && (currentControl as any).loaded && !(currentControl as any).loaded.get())
                        initialPromise = initialPromise.then(() => (currentControl as any).loadAsync());

                    if ((currentControl as any).activateAsync)
                        initialPromise = initialPromise.then(() => (currentControl as any).activateAsync());
                }

                initialPromise
                    .then(() =>
                    {
                        $$currentElement.addClass("hidden");
                        $$currentElement.css("visibility", StringHelper.empty);

                        let
                            animationIn = this.animationIn.get();

                        const
                            currentControlAsAny = this.currentControl as any;

                        if (currentControlAsAny?.dialogAnimationIn?.get !== void 0)
                            animationIn = currentControlAsAny.dialogAnimationIn.get();
                        else if (currentControlAsAny?.animationIn?.get !== void 0)
                            animationIn = currentControlAsAny.animationIn.get();

                        if (animationIn)
                        {
                            FomanticShim($$currentElement)
                                .transition("stop all")
                                .transition({
                                    animation: animationIn + " in",
                                    onComplete()
                                    {
                                        resolve();
                                    }
                                });
                        }
                        else
                        {
                            setTimeout(() =>
                            {
                                resolve();
                            });
                        }
                    })
                    .catch(error =>
                    {
                        console.error(error);

                        $$currentElement.addClass("hidden");
                        $$currentElement.css("visibility", StringHelper.empty);

                        let
                            message: string;

                        if (typeof error === "string")
                            message = error;
                        else if (error instanceof Error)
                            message = error.message;
                        else
                            message = "An error occurred";

                        $$(document).triggerHandler(
                            "Error",
                            {
                                message: message,
                                promise:
                                    PromiseHelper.createPromisePublic()
                                        .finally(() =>
                                        {
                                            if ($$currentElement)
                                            {
                                                return (
                                                    this.hideElementOrControlAsync($$currentElement)
                                                        .finally(() => reject(message))
                                                );
                                            }

                                            reject(message);
                                        })
                            });

                    });
            }
            else
                resolve();
        });
    }
    protected _disablePageScrolling()
    {
        const
            scrollBarDimensions = PageManager.instance?.scrollBarDimensions.get();

        $$(".rad-control-page.rad-feature-scrollable >.rad-control-container-wrapper")
            .each((index, element) =>
            {
                $$(element).css({
                    overflow: "hidden",
                    paddingRight: (scrollBarDimensions?.width ?? 0) + "px"
                })
            });
    }
    protected _disableTabStops()
    {
        if ($$("body").hasClass("rad-state-dimmed"))
            return;

        const
            element = this.$$element.get()?.get(0);

        if (!element)
            return;

        // Get all rad-controls excluding those contained within the dimmer
        $$(".rad-control")
            .not(element)
            .each((index, element) =>
            {
                const
                    control = JNativeStatic.radControl(element as HTMLElement);

                if (!control || !control.dimmed?.set)
                    return;

                control.dimmed.set(true);
            });

        // Get anything else that hasn't been dimmed and isn't within the dimmer
        $$(FOCUSABLE_ELEMENTS_SELECTOR + ", [tabindex]")
            .not("[data-tabindex-backup]")
            .not(element)
            .each((index, element) =>
            {
                const
                    $$this = $$(element),
                    tabIndex = $$this.attr("tabindex");

                if (tabIndex !== undefined)
                    $$this.attr("data-tabindex-backup", tabIndex);

                $$this.attr("tabindex", "-1");
            });
    }
    protected _enablePageScrolling()
    {
        $$(".rad-control-page.rad-feature-scrollable >.rad-control-container-wrapper")
            .each((index, element) =>
            {
                $$(element).css({
                    overflow: StringHelper.empty,
                    paddingRight: StringHelper.empty
                })
            });
    }
    protected _enableTabStops()
    {
        const
            element = this.$$element.get()?.get(0);

        if (!element)
            return;

        // get all rad-controls excluding those contained within the dimmer
        $$(".rad-control")
            .not(element)
            .each((index, element) =>
            {
                const
                    control = JNativeStatic.radControl(element as HTMLElement);

                if (!control || !control.dimmed?.set)
                    return;

                control.dimmed.set(false);
            });

        // get anything else that is still dimmed
        $$(FOCUSABLE_ELEMENTS_SELECTOR + ", [data-tabindex-backup]")
            .not(element)
            .each((index, element) =>
            {
                const
                    $$this = $$(element),
                    tabIndexBackup = $$this.attr("data-tabindex-backup");

                if (!ObjectHelper.isNullOrUndefined(tabIndexBackup))
                {
                    $$this.attr("tabindex", tabIndexBackup);
                    $$this.removeAttr("data-tabindex-backup");
                }
                else
                {
                    const
                        tabIndexControl = $$this.attr("data-tabindex-control");

                    if (ObjectHelper.isNullOrUndefined(tabIndexControl))
                        $$this.removeAttr("tabindex");
                }
            });
    }
    protected _getElementFromElementOrControl($$elementOrControl: any[] | IControl)
    {
        if (ArrayHelper.isArrayLike($$elementOrControl))
            return $$(ArrayHelper.firstOrDefault($$elementOrControl));
        else if (ObjectHelper.isAssignableTo<IControl>($$elementOrControl, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Control.Control"))
            return $$elementOrControl.$$element.get();
        return null;
    }
    protected _hide()
    {
        // Dimmer only hides if empty.
        if ((this.$$element.get()?.children().length ?? 0) > 0)
            return;

        this.removeCssClass("visible active");
        this.$$element.get()?.detach();
        PageDimmer.dimmersActive--;

        if (PageDimmer.dimmersActive === 0)
        {
            this._enableTabStops();
            $$("body").removeClass("rad-state-dimmed");
            this._enablePageScrolling();
        }
    }
    protected _repositionChildrenChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _show()
    {
        // Dimmer only shows if not already visible.
        if (this.shown())
            return;

        if (PageDimmer.dimmersActive === 0)
        {
            this._disableTabStops();
            $$("body").addClass("rad-state-dimmed");
            this._disablePageScrolling();
        }

        if (!PageDimmer.dimmerWrapperElement)
        {
            PageDimmer.dimmerWrapperElement = JNativeStatic.create({ nodeName: "div", attributes: { "class": "rad-control-page-dimmer-wrapper" } }) as HTMLElement;
            document.body.appendChild(PageDimmer.dimmerWrapperElement);
        }

        this.appendTo(PageDimmer.dimmerWrapperElement);
        this.addCssClass("visible active");

        PageDimmer.dimmersActive++;
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PageDimmerPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.animationIn =
            new PropertyString(
                this,
                propertyValues.animationIn,
                this._animationInChanged,
                undefined,
                [
                    new ClientAwareCategoryAttribute({ category: "Appearance" }),
                    new ClientAwareDisplayNameAttribute({ displayName: "Animation In" }),
                ],
            );

        this.animationOut =
            new PropertyString(
                this,
                propertyValues.animationOut,
                this._animationOutChanged,
                undefined,
                [
                    new ClientAwareCategoryAttribute({ category: "Appearance" }),
                    new ClientAwareDisplayNameAttribute({ displayName: "Animation Out" }),
                ],
            );

        this.repositionChildren =
            new PropertyBoolean(
                this,
                propertyValues.repositionChildren,
                this._repositionChildrenChanged,
                undefined,
                [
                    new ClientAwareCategoryAttribute({ category: "Behavior" }),
                    new ClientAwareDisplayNameAttribute({ displayName: "Reposition Children" }),
                ],
            );
    }

    protected override _bindEvents()
    {
        super._bindEvents();
        this.on("mousedown touchstart", { self: this }, this.onMouseDownOrTouchStart);
        $$(window).on("resizeend", { self: this }, this.onWindowResizeEnd);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        /* PH: fix for the IOS bug where the event handler never fires on the dimmer.
           See http://stackoverflow.com/questions/14795944/jquery-click-events-not-working-in-ios */
        // $$element.attr("onclick", String.empty); // This causes the console error: Refused to execute inline event handler because it violates the following Content Security Policy directive
        if (radWindow.device?.platform?.toLowerCase() === "ios")
            $$element?.css("cursor", "pointer");

        return $$element;
    }
    protected override _dispose()
    {
        this.off("mousedown touchstart", { self: this }, this.onMouseDownOrTouchStart);
        $$(window).off("resizeend", { self: this }, this.onWindowResizeEnd);

        super._dispose();
    }

    // #endregion

    // #region Event Handlers

    protected onMouseDownOrTouchStart(evt: IJNativeEvent)
    {
        if (evt.currentTarget !== evt.target)
            return;

        const
            self = evt.data.self as this;

        if (!(ObjectHelper.isAssignableTo<IDialog>(self.currentControl, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Dialog.Dialog") || ObjectHelper.isAssignableTo<IDialog2>(self.currentControl, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Dialog2.Dialog2")))
            return;

        self.currentControl.cancelAsync();
    }
    protected onWindowResizeEnd(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._currentElementReposition(true);
    }

    // #endregion

    // #endregion
}